import React, {useEffect, useRef, useState} from "react";
import axios from "axios";
import * as Constants from "../../utils/Constants";
import {getAuthToken, getClientIdOfUser} from "../../utils/utility";
import {createPortal} from "react-dom";
import useScrollLock from "../../hooks/useScrollLock";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {
    faCalendar,
    faChevronRight,
    faClock,
    faExclamationCircle,
    faGlobe,
    faMap,
    faMapMarkedAlt,
    faRoute,
    faSpinner,
    faXmark,
} from "@fortawesome/free-solid-svg-icons";
import "../../styles/components/route-history.css";
import "../../styles/base/animations.css";

// Local calendar date as YYYY-MM-DD. Deliberately NOT `new Date().toISOString()` — that returns the
// UTC date, so in a timezone ahead of UTC (e.g. IST, UTC+5:30) it reads as *yesterday* between local
// midnight and UTC midnight (00:00–05:30 IST). That made the pickers default to yesterday and the
// `max` exclude the current day right after 12 am, so "today" couldn't be selected.
const localDateStr = (d = new Date()) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

const RouteHistoryModal = ({
  assetUnitId,
  assetType,
  assetName,
  baseUrl,
  authToken,
  onClose,
}) => {
  useScrollLock();

  const [fromDate, setFromDate] = useState(() => localDateStr());
  const [toDate, setToDate] = useState(() => localDateStr());
  const [sessions, setSessions] = useState([]);
  const [sessionRoutes, setSessionRoutes] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [selectedSessionId, setSelectedSessionId] = useState(null);
  const [mapType, setMapType] = useState("hybrid");

  const mapRef = useRef(null);
  const mapInstance = useRef(null);
  const markersRef = useRef([]);
  const polylineRef = useRef(null);
  const shadowPolylineRef = useRef(null);

  // Safely convert any timestamp format to milliseconds.
  // Handles: ISO strings, Unix seconds (< 2e10), Unix milliseconds.
  const toMs = (ts) => {
    if (ts === null || ts === undefined) return 0;
    if (typeof ts === "number") return ts < 2e10 ? ts * 1000 : ts;
    return new Date(ts).getTime();
  };

  const formatTime = (date) => {
    return date.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  };

  const formatDate = (date) => {
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    });
  };

  // Initialize map
  useEffect(() => {
    if (!window.google || !mapRef.current) return;

    const map = new window.google.maps.Map(mapRef.current, {
      zoom: 12,
      center: { lat: 0, lng: 0 },
      mapTypeId: window.google.maps.MapTypeId.HYBRID,

      disableDefaultUI: false,
      zoomControl: false,
      fullscreenControl: false,
      mapTypeControl: false,
      streetViewControl: false,
      gestureHandling: "greedy",
    });

    mapInstance.current = map;

    return () => {
      mapInstance.current = null;
    };
  }, []);

  // Fetch route history
  const fetchRouteHistory = async () => {
    if (!assetUnitId) return;

    setLoading(true);
    setError("");
    setSessions([]);
    setSessionRoutes({});
    clearMap();

    try {
      const fromIso = new Date(`${fromDate}T00:00:00`).toISOString();
      const toIso = new Date(`${toDate}T23:59:59`).toISOString();

      const url =
        assetType === "GT06_TRACKER"
          ? `${Constants.BASE_URL}api/v1/${getClientIdOfUser()}/gt06-location-reports/route-history/${assetUnitId}/range?from=${encodeURIComponent(
              fromIso
            )}&to=${encodeURIComponent(toIso)}`
          : `${Constants.BASE_URL}api/auth/v1/mobi-track/history/${assetUnitId}/range?from=${encodeURIComponent(
              fromIso
            )}&to=${encodeURIComponent(toIso)}`;

      console.log("Fetching route history:", url);

      const response = await axios.get(url, {
        headers: {
          Authorization: `${Constants.BEARER} ${getAuthToken()}`,
        },
      });

      const sessionMap = response.data;

      if (!sessionMap || Object.keys(sessionMap).length === 0) {
        setError("No history found for the selected date range");
        setLoading(false);
        return;
      }

      const processedSessions = [];
      const routes = {};

      for (const [sessionId, points] of Object.entries(sessionMap)) {
        if (!points || points.length <= 1) continue;

        // Sort chronologically using safe timestamp conversion
        const sortedPoints = [...points].sort(
          (a, b) => toMs(a.timestamp) - toMs(b.timestamp)
        );

        const startTime = new Date(toMs(sortedPoints[0].timestamp));
        const endTime = new Date(toMs(sortedPoints[sortedPoints.length - 1].timestamp));

        const durationMinutes = Math.floor((endTime - startTime) / 1000 / 60);

        processedSessions.push({
          sessionId,
          startTimestamp: sortedPoints[0].timestamp,
          date: startTime.toLocaleDateString("en-US"),
          fromTime: startTime.toLocaleTimeString("en-US", {
            hour: "2-digit",
            minute: "2-digit",
            hour12: true,
          }),
          toTime: endTime.toLocaleTimeString("en-US", {
            hour: "2-digit",
            minute: "2-digit",
            hour12: true,
          }),
          duration:
            durationMinutes >= 60
              ? `${(durationMinutes / 60).toFixed(1)} hr`
              : `${durationMinutes} min`,
          pointCount: sortedPoints.length,
        });

        routes[sessionId] = sortedPoints;
      }

      // Sort newest first using safe timestamp conversion
      processedSessions.sort(
        (a, b) => toMs(b.startTimestamp) - toMs(a.startTimestamp)
      );

      if (processedSessions.length === 0) {
        setError("No valid sessions found (insufficient points)");
        setLoading(false);
        return;
      }

      // Assign ride names
      const namedSessions = processedSessions.map((session, index) => ({
        ...session,
        rideName: `Ride ${processedSessions.length - index}`,
      }));

      setSessions(namedSessions);
      setSessionRoutes(routes);
      setLoading(false);
    } catch (err) {
      console.error("Failed to fetch route history:", err);

      if (err?.response?.status === 403) {
        setError("Access denied. Please check your permissions.");
      } else if (err?.response?.status === 404) {
        setError("No history found for the selected date range");
      } else {
        setError("Unable to fetch route history");
      }

      setLoading(false);
    }
  };

  // Clear map markers and polylines
  const clearMap = () => {
    markersRef.current.forEach((marker) => marker.setMap(null));
    markersRef.current = [];

    if (polylineRef.current) {
      polylineRef.current.setMap(null);
      polylineRef.current = null;
    }

    if (shadowPolylineRef.current) {
      shadowPolylineRef.current.setMap(null);
      shadowPolylineRef.current = null;
    }
  };

  // Draw single session on map
  const drawSingleSession = (sessionId) => {
    const map = mapInstance.current;
    if (!map) return;

    const points = sessionRoutes[sessionId];
    if (!points || points.length === 0) return;

    clearMap();
    setSelectedSessionId(sessionId);

    const latLngList = points.map((p) => ({
      lat: p.latitude,
      lng: p.longitude,
    }));

    // Shadow polyline
    const shadowPolyline = new window.google.maps.Polyline({
      path: latLngList,
      geodesic: true,
      strokeColor: "#000000",
      strokeOpacity: 0.2,
      strokeWeight: 18,
      map: map,
    });
    shadowPolylineRef.current = shadowPolyline;

    // Main polyline
    const polyline = new window.google.maps.Polyline({
      path: latLngList,
      geodesic: true,
      strokeColor: "#1a73e8",
      strokeOpacity: 1.0,
      strokeWeight: 12,
      map: map,
    });
    polylineRef.current = polyline;

    // Start marker
    const startMarker = new window.google.maps.Marker({
      position: latLngList[0],
      map: map,
      title: "Start",
      icon: {
        path: window.google.maps.SymbolPath.CIRCLE,
        fillColor: "#4CAF50",
        fillOpacity: 1,
        strokeColor: "#FFFFFF",
        strokeWeight: 3,
        scale: 10,
      },
    });
    markersRef.current.push(startMarker);

    // End marker
    const endMarker = new window.google.maps.Marker({
      position: latLngList[latLngList.length - 1],
      map: map,
      title: "End",
      icon: {
        path: window.google.maps.SymbolPath.CIRCLE,
        fillColor: "#F44336",
        fillOpacity: 1,
        strokeColor: "#FFFFFF",
        strokeWeight: 3,
        scale: 10,
      },
    });
    markersRef.current.push(endMarker);

    // Fit bounds
    const bounds = new window.google.maps.LatLngBounds();
    latLngList.forEach((pos) => bounds.extend(pos));
    map.fitBounds(bounds, 150);
  };

  // Toggle map type
  const toggleMapType = () => {
    const map = mapInstance.current;
    if (!map) return;

    const newType = mapType === "hybrid" ? "roadmap" : "hybrid";
    setMapType(newType);
    map.setMapTypeId(
      newType === "hybrid"
        ? window.google.maps.MapTypeId.HYBRID
        : window.google.maps.MapTypeId.ROADMAP
    );
  };

  // Fetch on mount
  useEffect(() => {
    fetchRouteHistory();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return createPortal(
    <div className="route-history-overlay" onClick={onClose}>
      <div
        className="route-history-container"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="route-history-header">
          <div className="route-history-title-section">
            <h2 className="route-history-title">Route History</h2>
            <p className="route-history-subtitle">
              {assetName} - {assetUnitId}
            </p>
          </div>
          <button onClick={onClose} className="route-history-close-btn">
            <FontAwesomeIcon icon={faXmark} />
          </button>
        </div>

        {/* Date Filters */}
        <div className="route-history-filters">
          <div className="date-filter-group">
            <FontAwesomeIcon icon={faCalendar} className="date-filter-icon" />
            <label className="date-filter-label">From:</label>
            <input
              type="date"
              value={fromDate}
              max={localDateStr()}
              onChange={(e) => {
                const newFromDate = e.target.value;
                setFromDate(newFromDate);
                if (toDate < newFromDate) {
                  setToDate(newFromDate);
                }
              }}
              className="date-filter-input"
            />
          </div>

          <div className="date-filter-group">
            <label className="date-filter-label">To:</label>
            <input
              type="date"
              value={toDate}
              min={fromDate}
              max={localDateStr()}
              onChange={(e) => setToDate(e.target.value)}
              className="date-filter-input"
            />
          </div>

          <button
            onClick={fetchRouteHistory}
            disabled={loading}
            className="fetch-routes-btn"
          >
            {loading && <FontAwesomeIcon icon={faSpinner} spin />}
            Fetch Routes
          </button>
        </div>

        {/* Content */}
        <div className="route-history-content">
          {/* Sessions List */}
          <div className="sessions-sidebar">
            <div className="sessions-header">
              <h3 className="sessions-title">Sessions ({sessions.length})</h3>
            </div>

            <div className="sessions-list">
              {loading && (
                <div className="loading-state">
                  <div className="loading-orbital" />
                  <div className="loading-state-label">Fetching route history...</div>
                </div>
              )}

              {error && !loading && (
                <div className="error-state">
                  <FontAwesomeIcon
                    icon={faExclamationCircle}
                    size="2x"
                    className="error-icon"
                  />
                  <p className="error-message">{error}</p>
                </div>
              )}

              {!loading && !error && sessions.length === 0 && (
                <div className="empty-state">
                  <FontAwesomeIcon
                    icon={faRoute}
                    size="2x"
                    className="empty-icon"
                  />
                  <p>No sessions found</p>
                </div>
              )}

              {sessions.map((session) => (
                <div
                  key={session.sessionId}
                  onClick={() => drawSingleSession(session.sessionId)}
                  className={`session-card ${
                    selectedSessionId === session.sessionId ? "selected" : ""
                  }`}
                >
                  <div className="session-card-header">
                    <h4 className="session-card-title">{session.rideName}</h4>
                    <FontAwesomeIcon
                      icon={faChevronRight}
                      className="session-card-chevron"
                    />
                  </div>

                  <div className="session-card-date">
                    <FontAwesomeIcon
                      icon={faCalendar}
                      className="session-card-icon"
                    />
                    {session.date}
                  </div>

                  <div className="session-card-time">
                    <FontAwesomeIcon
                      icon={faClock}
                      className="session-card-icon"
                    />
                    {session.fromTime} - {session.toTime}
                  </div>

                  <div className="session-card-footer">
                    <span>
                      Duration:{" "}
                      <span className="session-card-duration">
                        {session.duration}
                      </span>
                    </span>
                    <span>{session.pointCount} points</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Map */}
          <div className="route-history-map-area">
            <div ref={mapRef} className="route-history-map" />

            {/* Map Type Toggle */}
            <button onClick={toggleMapType} className="route-map-type-toggle">
              <FontAwesomeIcon icon={mapType === "hybrid" ? faMap : faGlobe} />
            </button>

            {!selectedSessionId && sessions.length > 0 && (
              <div className="no-selection-state">
                <FontAwesomeIcon
                  icon={faMapMarkedAlt}
                  size="3x"
                  className="no-selection-icon"
                />
                <p className="no-selection-text">
                  Select a session to view route
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default RouteHistoryModal;
